import { useEffect, useState } from 'react';
import type { IdeaEntryInput, IdeaEntryIntent } from '@mouja/domain/idea-entry';
import type {
  IdeaRevision,
  IdeaSet,
  IdeaPresentation,
} from '@mouja/domain/content-ideas';
import { request } from '../../workspace/api.js';
import { firstIdeaEntryEnabled } from '../../merchant/api.js';
import type { SavedDirection } from './savedBriefApi.js';
export interface FirstIdeaView {
  schemaVersion: 'first-idea-journey.v1';
  journeyId: string;
  intent: IdeaEntryIntent;
  phase:
    | 'RESEARCHING'
    | 'IDEAS_READY'
    | 'NEEDS_INPUT'
    | 'RECOVERY'
    | 'ADJUSTING'
    | 'SELECTED';
  requestedCount: number;
  availableCount: number;
  revision: IdeaRevision | null;
  currentRevisionId: string | null;
  isCurrent: boolean;
  selected: { selectionId: string; directionArtifactId: string } | null;
  history: {
    revisionId: string;
    parentRevisionId: string | null;
    createdAt: string;
    current: boolean;
    selected: boolean;
  }[];
  ideaSet: IdeaSet | null;
  presentation: IdeaPresentation | null;
  directions: (SavedDirection & { ideaId: string })[];
  adjustments: {
    publicId: string;
    parentRevisionId: string;
    ideaId: string;
    adjustmentText: string;
    state: string;
    failureCode: string | null;
    resultRevisionId: string | null;
  }[];
  actions: {
    canSelect: boolean;
    canAdjust: boolean;
    canPrepareScript: boolean;
    canReload: boolean;
  };
}
export interface IdeaStartInput {
  sourceIntakeId: string;
  expectedProfileArtifactId: string;
  firstStart: boolean;
  entry: IdeaEntryInput;
}
export interface PendingIdeaStart {
  key: string;
  body: IdeaStartInput;
}
export function usePendingIdeaStart(sourceIntakeId: string | undefined) {
  const [pending, setPending] = useState<PendingIdeaStart | null>(null);
  useEffect(() => {
    const controller = new AbortController();
    setPending(null);
    if (sourceIntakeId)
      void request<{ pending: PendingIdeaStart | null }>(
        'GET',
        `/merchant/idea-journey-starts?sourceIntakeId=${encodeURIComponent(sourceIntakeId)}`,
        { signal: controller.signal },
      )
        .then((r) => {
          if (!controller.signal.aborted) setPending(r.pending);
        })
        .catch(() => {});
    return () => {
      controller.abort();
    };
  }, [sourceIntakeId]);
  return pending;
}
export const startIdeaJourney = (body: IdeaStartInput, idempotencyKey: string) =>
  request<{ journeyId: string; productionLineId: string; state: string }>(
    'POST',
    '/merchant/idea-journeys',
    { body, idempotencyKey },
  );
export const requestIdeaAdjustment = (
  journeyId: string,
  body: unknown,
  idempotencyKey: string,
) =>
  request(
    'POST',
    `/merchant/idea-journeys/${encodeURIComponent(journeyId)}/adjustments`,
    { body, idempotencyKey },
  );
export const fetchAdjustmentCapability = (id: string) =>
  request<{ available: boolean }>(
    'GET',
    `/merchant/idea-journeys/${encodeURIComponent(id)}/adjustments`,
  );
export function useFirstIdeaCapability() {
  const [enabled, setEnabled] = useState<boolean | 'unavailable' | null>(null);
  useEffect(() => {
    const controller = new AbortController();
    void firstIdeaEntryEnabled(controller.signal)
      .then((r) => {
        if (!controller.signal.aborted) setEnabled(r);
      })
      .catch(() => {
        if (!controller.signal.aborted) setEnabled('unavailable');
      });
    return () => {
      controller.abort();
    };
  }, []);
  return enabled;
}
